import React from 'react'
import styled from 'styled-components'
import { useAuth } from '../context/AuthContext'
import { Link } from 'react-router-dom'

const DashboardContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`

const WelcomeSection = styled.section`
  background: white;
  padding: 2rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
`

const PlanCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  margin-bottom: 2rem;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  text-align: center;
`

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #2563eb;
`

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
`
const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`


const ActionCard = styled(Link)`
  background: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  text-decoration: none;
  color: inherit;
  border: 2px solid transparent;
  transition: all 0.2s;

  &:hover {
    border-color: #2563eb;
    transform: translateY(-2px);
  }
`

const ActionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1f2937;
`

const ActionDescription = styled.p`
  color: #6b7280;
  margin-bottom: 0;
`

const WorkflowSteps = styled.div`
  display: grid;
  gap: 1rem;
`

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`

const StepNumber = styled.div`
  width: 2rem;
  height: 2rem;
  background: #2563eb;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
`

const UpgradeBanner = styled.div`
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  margin-bottom: 2rem;
  text-align: center;
`

function Dashboard() {
  const { user } = useAuth()

  const getPlanDisplayName = (plan) => {
    const planNames = {
      'free_trial': 'Free Trial',
      'basic': 'Basic',
      'professional': 'Professional',
      'institutional': 'Institutional'
    }
    return planNames[plan] || plan
  }

  const shouldShowUpgrade = user?.subscription_plan === 'free_trial' || 
                           user?.subscription_plan === 'basic'

  return (
    <DashboardContainer>
      <WelcomeSection>
        <SectionTitle>Welcome back, {user?.display_name || 'User'}!</SectionTitle>
        <p>Manage your student communications efficiently with AI-powered assistance.</p>
      </WelcomeSection>

      {shouldShowUpgrade && (
        <UpgradeBanner>
          <h3 className="text-xl font-semibold mb-2">Upgrade Your Plan</h3>
          <p>Get access to more AI responses, cohorts, and advanced features!</p>
          <button style={{
            marginTop: '1rem',
            background: 'white',
            color: '#d97706',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}>
            View Plans
          </button>
        </UpgradeBanner>
      )}

      <PlanCard>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 'semibold', marginBottom: '0.5rem' }}>
          Current Plan: {getPlanDisplayName(user?.subscription_plan || 'free_trial')}
        </h3>
        <p>
          You have used <strong>{user?.ai_responses_used || 0}</strong> of{' '}
          <strong>{user?.ai_responses_limit || 20}</strong> AI responses this month.
        </p>
        {user?.subscription_plan === 'free_trial' && (
          <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', opacity: '0.9' }}>
            Free trial period: 14 days
          </p>
        )}
      </PlanCard>

      <StatsGrid>
        <StatCard>
          <StatNumber>{user?.ai_responses_used || 0}</StatNumber>
          <StatLabel>AI Responses Used</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{user?.ai_responses_limit - (user?.ai_responses_used || 0)}</StatNumber>
          <StatLabel>Responses Remaining</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{user?.cohorts_limit || 2}</StatNumber>
          <StatLabel>Max Cohorts</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>
            {user?.subscription_plan === 'free_trial' ? '14' : 
             user?.subscription_plan === 'basic' ? '30' : '∞'}
          </StatNumber>
          <StatLabel>Days Remaining</StatLabel>
        </StatCard>
      </StatsGrid>

      <QuickActions>
        <ActionCard to="/emails">
          <ActionTitle>📧 Email Management</ActionTitle>
          <ActionDescription>
            View student emails and generate AI-powered responses
          </ActionDescription>
        </ActionCard>

        <ActionCard to="/cohorts">
          <ActionTitle>👥 Cohort Management</ActionTitle>
          <ActionDescription>
            Manage your cohorts and course materials
          </ActionDescription>
        </ActionCard>

        <ActionCard to="/cohorts">
          <ActionTitle>📊 Analytics</ActionTitle>
          <ActionDescription>
            View insights and improvement suggestions
          </ActionDescription>
        </ActionCard>

        <ActionCard to="/">
          <ActionTitle>⚙️ Settings</ActionTitle>
          <ActionDescription>
            Manage your account and preferences
          </ActionDescription>
        </ActionCard>
      </QuickActions>

      <SectionTitle>How to Use Academic Inbox AI</SectionTitle>
      <WorkflowSteps>
        <Step>
          <StepNumber>1</StepNumber>
          <div>
            <h4 style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Set Up Cohorts</h4>
            <p style={{ color: '#6b7280', margin: '0' }}>
              Create cohorts and upload course materials (PPTX, PDF, etc.)
            </p>
          </div>
        </Step>
        <Step>
          <StepNumber>2</StepNumber>
          <div>
            <h4 style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Check Emails</h4>
            <p style={{ color: '#6b7280', margin: '0' }}>
              View student emails in the email panel from your Outlook
            </p>
          </div>
        </Step>
        <Step>
          <StepNumber>3</StepNumber>
          <div>
            <h4 style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Generate Responses</h4>
            <p style={{ color: '#6b7280', margin: '0' }}>
              Use AI to draft professional responses based on course materials
            </p>
          </div>
        </Step>
        <Step>
          <StepNumber>4</StepNumber>
          <div>
            <h4 style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Send or Save</h4>
            <p style={{ color: '#6b7280', margin: '0' }}>
              Send responses directly or save as drafts in Outlook
            </p>
          </div>
        </Step>
      </WorkflowSteps>
    </DashboardContainer>
  )
}

export default Dashboard
